<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { useChecklistControllerStore } from "~/composables/useChecklistControllerStore";

import { GuidedTour } from "classes/models/tours/guided-tour";
import { Subscription } from "rxjs";
import ChecklistChangeModal from "./ChangeModal.client.vue";

const documentId = inject("documentId") as any;

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isTeacherViewMode, isCompanionMode } = storeToRefs(
  documentViewModeStore
);

const documentChecklistStore = useDocumentChecklistStore(documentId);

const { useChecklist, isInitialized, useAllCriteriaInstances } = storeToRefs(
  documentChecklistStore
);

const checklistController = useChecklistControllerStore(documentId);
const { savedScrollPosition } = storeToRefs(checklistController);

const documentActiveTypeStore = useDocumentActiveTypeStore(documentId);
const documentShowFormattingStore = useDocumentShowFormattingStore(documentId);

const userClassroomStore = useUserClassroomsDataStore();
const { allAssignments, allClassrooms } = storeToRefs(userClassroomStore);

const checklistContainer = ref();

onMounted(() => {
  setTimeout(() => {
    const checklsitcontainerElement = checklistContainer.value as HTMLElement;

    if (savedScrollPosition.value && checklsitcontainerElement) {
      checklsitcontainerElement.scrollTo({
        top: savedScrollPosition.value,
      });
    }
  }, 100);

  documentActiveTypeStore.clearAll();
  documentShowFormattingStore.setShouldShowFormatting(false);

  onInitializedChanged();
});

const { clearAll } = useDocumentHighlightStore(documentId);
clearAll();

useDocumentAnnotationHighlight().clearAnnotationsToHighlight();

const submittedDocumentStore = useSubmittedDocumentStore(documentId);
const { submittedDocument } = storeToRefs(submittedDocumentStore);

const onChangeLateDeduction = (event: any) => {
  const value = event.target.value;

  documentChecklistStore.updateLateScore(value);
};

const addCustomCriteria = () => {
  if (useChecklist.value == undefined) return;

  if (useChecklist.value.customCriteriaInstances == undefined) {
    useChecklist.value.customCriteriaInstances = [];
  }

  useChecklist.value.customCriteriaInstances.push({
    displayOrder: useChecklist.value.customCriteriaInstances.length,
    evaluationMethod: "any",
    isCustom: true,
    labelOverride: "",
    numParagraphs: 1,
    pointsPerInstance: 0,
    suppressPointsDeductions: false,
    ignore: false,
  });

  documentChecklistStore.saveChecklist();
};

const removeCustomCriteria = (index: number) => {
  if (useChecklist.value == undefined) return;

  if (useChecklist.value.customCriteriaInstances == undefined) return;

  useChecklist.value.customCriteriaInstances.splice(index, 1);
  documentChecklistStore.saveChecklist();
};

const changeChecklist = () => {
  const { open, close } = useDialog({
    title: "Change Checklist",
    component: ChecklistChangeModal,
    props: {
      documentId: documentId,
      onClose: () => {
        close();
      },
    },
  });

  open();
};

const guidedTourStore = useGuidedTours();
const { allTours, tourToShow } = storeToRefs(guidedTourStore);

const tourEventSubscription = ref<Subscription | undefined>(undefined);

watch(isInitialized, () => {
  onInitializedChanged();
});

const onInitializedChanged = () => {
  if (isInitialized.value != true) return;

  initializeTour();
  listenForTourEvents();
};

const initializeTour = () => {
  if (submittedDocument.value?.tours == undefined) return;

  if (useGuidedTours().hasActiveTour == true) return;

  const incompleteTours = submittedDocument.value.tours.filter(
    (tour: any) => tour.isComplete != true
  );

  if (incompleteTours.length == 0) {
    showOptionalTours();
    return;
  }

  const firstTour = incompleteTours[0];
  const tour = allTours.value.find((tour) => tour.id == firstTour.id);

  if (tour == undefined) return;

  if (firstTour?.type == "dialog") {
    guidedTourStore.startTour(tour);
  } else {
    guidedTourStore.setTourToShow(firstTour.id);
  }
};

const listenForTourEvents = () => {
  if (tourEventSubscription.value) {
    tourEventSubscription.value.unsubscribe();
  }

  tourEventSubscription.value = useTourEventBus().events.subscribe(
    async (event) => {
      if (!event) return;

      switch (event.name) {
        case "start-grading":
          // Here is where I may call a function to reset the checklist to the template or original. I guessI could call reevaluate all as an option

          useGuidedTours().setTourToShow(event.data);

          break;
        case "tour-complete":
          if (event.data == undefined) return;

          if (submittedDocument.value == undefined) return;

          const tour = submittedDocument.value?.tours?.find(
            (tour) => tour.id == event.data
          );

          if (tour == undefined) return;

          tour.isComplete = true;

          await useSubmittedDocumentStore(documentId).saveDocument();

          initializeTour();

          break;
      }
    }
  );
};

const showOptionalTours = () => {
  if (submittedDocument.value?.tours == undefined) return;

  // Check if there are any optionalTours
  const optionalTourIds = submittedDocument.value.optionalTours ?? [];

  const optonalTours = allTours.value.filter((tour) =>
    optionalTourIds.includes(tour.id!)
  );

  if (optonalTours.length == 0) return;

  guidedTourStore.setRestartableTours(optonalTours);
};

const showTourButton = computed(() => {
  if (submittedDocument.value?.tours == undefined) return false;

  const tour = submittedDocument.value.tours.find(
    (tour: any) => tour.id == tourToShow.value?.id
  );

  if (tour == undefined) return false;

  return tour.isComplete != true && tour.type == "checklist-button";
});

const startTour = (tour: GuidedTour) => {
  guidedTourStore.startTour(tour);
};

const isSubmittingToTeacher = ref(false);
const submitDocument = async () => {
  const resposne = await useBaseConfirm(
    "Are you sure?",
    "Are you sure you are ready to submit?",
    false,
    "Yes",
    "No"
  );

  if (resposne != true) return;
  isSubmittingToTeacher.value = true;

  await useSubmittedDocumentStore(documentId).updateDocumentState(
    DocumentState.submitted
  );

  navigateTo("/documents/student/submitted");
};

const isKeyWordOutline = computed(() => {
  return (
    submittedDocument.value?.documentSubmissionType ==
    DocumentSubmissionType.keyWordOutline
  );
});

const showSubmitButton = computed(() => {
  return (
    isTeacherViewMode.value != true &&
    submittedDocument.value?.state == DocumentState.studentReview
  );
});

const allowChecklists = computed(() => {
  const allowChecklistForSubmission = useAllowChecklistForSubmission(
    submittedDocument.value
  );

  if (allowChecklistForSubmission != undefined) {
    return allowChecklistForSubmission;
  }

  const matchingAssignment = allAssignments.value.find(
    (assignment) => assignment.id == submittedDocument.value?.assignmentId
  );

  if (matchingAssignment == undefined) return true;

  if (matchingAssignment.requireRoughDraftChecklist != undefined) {
    return matchingAssignment.requireRoughDraftChecklist;
  }

  return true;
});

const hasUnreviewedCriteria = computed(() => {
  let isAllReviewed = true;

  for (const criteriaInstance of useAllCriteriaInstances.value) {
    if (criteriaInstance.isReviewedByStudent != true) {
      isAllReviewed = false;
      break;
    }
  }

  return !isAllReviewed;
});

const hasIncompleteCriteria = computed(() => {
  let isAllComplete = true;

  for (const criteriaInstance of useAllCriteriaInstances.value) {
    if (criteriaInstance.isIncomplete == true) {
      isAllComplete = false;
      break;
    }
  }

  return !isAllComplete;
});

const paragraphLabel = computed(() => {
  if (useChecklist.value == undefined) return "1 paragraph";

  if (useChecklist.value?.numParagraphs == undefined) return "1 paragraph";

  return `${useChecklist.value?.numParagraphs}-paragraph`;
});

const unitLabel = computed(() => {
  return useChecklist.value?.unit;
});

const unitModelController = useNewSheetController();
const activeUnitModel = ref<any | undefined>(undefined);
const openUnitModel = (unit: any) => {
  activeUnitModel.value = unit;
  unitModelController.value.open();
  console.log("openUnitModel");
};
</script>

<template>
  <div>
    <BaseLoading v-if="isInitialized != true" />
    <BaseSheetComponent
      :controller="unitModelController"
      width="500px"
      :include-content-padding="false"
      position="left"
      :transparent="true"
    >
      <template #content>
        <ChecklistUnitModal
          v-if="activeUnitModel != undefined"
          :unit="activeUnitModel"
        />
      </template>
    </BaseSheetComponent>
    <div
      v-if="isInitialized"
      id="checklist-container"
      ref="checklistContainer"
      class="overflow-auto h-full p-4 w-full"
    >
      <h2
        v-if="allowChecklists && useChecklist"
        class="flex flex-row items-center text-h5 font-bold mb-4 border-b pb-1 justify-between w-full"
      >
        {{ useChecklist?.title }}
        <BasePopover align="end">
          <BaseTextButton
            v-if="isTeacherViewMode"
            class="ml-2 mt-[2px] !py-1 text-body-1 font-normal"
            color="primary"
          >
            <BaseIconEdit class="mr-1" />
            Change
          </BaseTextButton>
          <template #content>
            <ChecklistChangeModal
              :document-id="documentId"
              :existing-checklist-id="useChecklist?.id"
            />
          </template>
        </BasePopover>
      </h2>
      <p
        v-if="
          isCompanionMode || submittedDocument?.state == DocumentState.editing
        "
        class="text-sm text-primary mb-4 text-left"
      >
        Write a
        <span class="text-warning">{{ paragraphLabel }}</span> composition.
        Follow the
        <ChecklistUnitsButton :unit="unitLabel" @showUnit="openUnitModel" />
        model.<br />
        Do what you check and check what you do.
      </p>

      <div
        v-if="
          isTeacherViewMode &&
          allowChecklists == true &&
          useChecklist == undefined
        "
        class="pb-4 border-b mb-4 flex flex-col items-center justify-center"
      >
        <BasePopover>
          <BaseTextButton
            v-if="isTeacherViewMode"
            class="ml-2 mt-[2px] !p-1 text-body-1 font-normal"
          >
            <div class="flex flex-row items-center">
              <BaseIconAdd class="mr-1" />
              Attach Checklist
            </div>
          </BaseTextButton>
          <template #content>
            <ChecklistChangeModal :document-id="documentId" />
          </template>
        </BasePopover>
      </div>
      <div v-if="allowChecklists && useChecklist">
        <ChecklistSection
          v-for="(section, index) in useChecklist?.sections"
          :key="index"
          :section="section"
        />
      </div>
      <div class="flex flex-col relative">
        <div
          v-if="showTourButton && tourToShow"
          class="w-full h-full bg-primary-default absolute top-0 left-0 z-[9999] bg-opacity-20 rounded"
        >
          <div class="flex flex-col w-full h-full items-center justify-center">
            <BaseButton
              color="secondary"
              class="m-4"
              @click="startTour(tourToShow)"
            >
              {{ tourToShow.buttonLabel }}
            </BaseButton>
          </div>
        </div>
        <div
          v-if="isTeacherViewMode && useChecklist && allowChecklists"
          class="mb-4"
        >
          <div class="flex flex-row items-center mb-1">
            <span class="font-bold"> Additional Feedback </span>
            <BaseIconButton
              v-if="isTeacherViewMode"
              class="w-[150px] ml-1 !p-1"
              @click="addCustomCriteria"
            >
              <icon name="gg:add" />
            </BaseIconButton>
          </div>

          <div
            v-if="useChecklist?.customCriteriaInstances && allowChecklists"
            class="flex flex-col"
            v-auto-animate
          >
            <span
              v-if="
                useChecklist?.customCriteriaInstances == undefined ||
                useChecklist.customCriteriaInstances.length == 0
              "
              class="italic"
            >
              No additional feedback
            </span>
            <ChecklistCriteria
              v-for="(
                criteriaInstance, index
              ) in useChecklist.customCriteriaInstances"
              :key="index"
              :criteria-instance="criteriaInstance"
              @remove="removeCustomCriteria(index)"
            />
          </div>
        </div>
        <div
          v-if="isTeacherViewMode && submittedDocument?.isLate && allowChecklists"
        >
          <span class="mb-1 font-bold"> Late Points Deduction </span>
          <div class="mb-2 pb-2 border-b-2 flex flex-row items-center px-2">
            <div class="grow whitespace-normal">
              This document was submitted late
              <span
                v-if="
                  submittedDocument?.submittedAtTimestamp &&
                  submittedDocument?.submittedAtTimestamp.toMillis
                "
              >
                on
                {{
                  useBasicDateTimeFormatter(
                    submittedDocument?.submittedAtTimestamp.toMillis()
                  )
                }}
              </span>
            </div>
            <BaseTextInput
              class="shrink-0 w-[50px] h-[35px] text-center ml-2"
              :model-value="useChecklist?.isLateDeduction"
              @input.number="onChangeLateDeduction"
            />
          </div>
        </div>
        <DocumentPanelFeedbackContainer
          v-if="isTeacherViewMode"
          class="mt-2 mb-2 max-h-[500px]"
          data-step-id="document-panel-feedback-container"
        />
        <DocumentPanelFeedbackStudentNoteForm
          v-if="isTeacherViewMode && submittedDocument?.studentId"
        />
        <DocumentGradedToggle v-if="isTeacherViewMode" />
        <BaseLabelBadge
          v-if="
            showSubmitButton &&
            hasUnreviewedCriteria != true &&
            hasIncompleteCriteria == true
          "
          color="danger"
          class="mb-4"
        >
          <p class="mb-2">
            Confirm that the checklist items in red are complete.
          </p>
          <p>
            If they are, submit your paper. If you find that you need to revise
            your paper, delete this document. After you edit it, resubmit your
            paper.
          </p>
        </BaseLabelBadge>
        <div
          v-if="showSubmitButton && hasUnreviewedCriteria"
          class="p-2 text-center text-danger-default border-danger-default rounded border-dashed border-2 mb-4"
        >
          Please review all criteria before submitting.
        </div>
        <BaseButton
          v-if="showSubmitButton"
          :show-spinner="isSubmittingToTeacher"
          :disabled="hasUnreviewedCriteria"
          @click="submitDocument"
        >
          Submit
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
